import { AriaAttributes, FC } from "react"

import styled from "styled-components"

import {
  borderRadius,
  colors,
  IconCircleTickSmall,
  IconInfoSmall,
  IconSize,
  IconWarningSmall,
  spacing,
  textColorVariables,
  typography,
} from "@izettle/otto"
import { IconMetadata, MessageProps, MessageType, SvgIcon, Text } from "@izettle/react"

import { theme } from "@/theme"

type ExtraMessageProps = {
  ariaLive?: "off" | "assertive" | "polite" | undefined
}

const getBorderColor = ({ type }: { type: MessageType }) => {
  if (type === MessageType.INFO) {
    return colors.border.info
  } else {
    return colors.border[type]
  }
}

const Container = styled.figure<{ type: MessageType }>`
  display: flex;
  margin: ${spacing.none};
  flex-direction: row;
  box-sizing: border-box;
  word-break: break-word;
  ${typography.regularLongText}
  background: ${({ type }) => colors.background[type]};
  text-align: left;
  border: 1px solid ${getBorderColor};
  color: ${({ type }) => colors.text[type]};
  padding: ${spacing.small};
  border-radius: ${borderRadius.medium};
  ${({ type }) => `${textColorVariables.primary}: ${colors.text[type]};`}
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  min-height: ${spacing.large};
  flex: 1;
  justify-content: space-between;
  width: 100%;
  min-width: 150px;
  margin: ${spacing.none};

  &&& a {
    color: inherit;
    text-decoration: underline;
  }
`

const StyledIcon = styled(SvgIcon)`
  margin: ${spacing.none} ${spacing.xxSmall} ${spacing.none} ${spacing.none};
  ${typography.large}
  color : ${colors.text.default};
  &:before {
    line-height: 1;
    margin-top: calc(${spacing.tiny} / 2);
  }

  @media ${theme.devices.tinyMobile} {
    display: none;
  }

  @media ${theme.devices.mobile} {
    display: inline;
  }
`

const TitleText = styled(Text.RegularBold)`
  margin-top: ${spacing.none};
  margin-bottom: ${spacing.none};
  color : ${colors.text.default}
`

type IconType = {
  svgIcon: IconMetadata<IconSize.Small>
}

type IconMap = {
  [key in MessageType]: IconType
}

const getIcon = (type: MessageType): IconType => {
  const iconsMap: IconMap = {
    success: {
      svgIcon: IconCircleTickSmall,
    },
    info: {
      svgIcon: IconInfoSmall,
    },
    warning: {
      svgIcon: IconInfoSmall,
    },
    error: {
      svgIcon: IconWarningSmall,
    },
  }
  if (iconsMap[type]) {
    return iconsMap[type]
  }
  return iconsMap.success
}

export const Message: FC<MessageProps & ExtraMessageProps> = ({
  children,
  title,
  type = MessageType.INFO,
  className,
  showIcon = false,
  svgIcon,
  ariaLive,
}) => {
  let iconName
  if (svgIcon) {
    iconName = {
      svgIcon,
    }
  } else {
    iconName = getIcon(type)
  }

  const ariaOptionalProps: AriaAttributes = {}
  if (ariaLive) {
    ariaOptionalProps["aria-live"] = ariaLive
  }

  return (
    <Container type={type} className={className} {...ariaOptionalProps}>
      {showIcon && iconName && <StyledIcon icon={iconName.svgIcon} />}
      <ContentWrapper>
        {Boolean(title) && (
          <TitleText>
            {title}
          </TitleText>
        )}
        {children}
      </ContentWrapper>
    </Container>
  )
}
